import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { ResponsiveBaseComponent, JJamesCardComponent } from 'shared-lib';
import { CustomerService } from '../../../api/gen/api/customer.service';
import { CustomerDto } from '../../../api/gen/model/customerDto';
import { OperatorGroupDto } from '../../../api/gen/model/operatorGroupDto';
import { OperatorGroupRole } from '../../../api/gen/model/operatorGroupRole';
import { SortOrder } from '../../../api/gen/model/sortOrder';

import { GroupItemComponent } from './group-item/group-item.component';
import { AssignParkingspaceGroupComponent, AssignParkingSpaceGroupDialogData } from './assign-parkingspace-group/assign-parkingspace-group.component';
import { UnassignParkingspaceGroupComponent, UnassignParkingSpaceGroupDialogData } from './unassign-parkingspace-group/unassign-parkingspace-group.component';
import { AddOperatorGroupComponent, AddOperatorGroupDialogData } from './add-operator-group/add-operator-group.component';

export interface CustomerGroupSection {
  customerId: string;
  customerName: string;
  groups: OperatorGroupDto[];
}

@Component({
  selector: 'app-groups-ui',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatFormFieldModule,
    MatExpansionModule,
    MatDialogModule,
    MatSnackBarModule,
    JJamesCardComponent,
    GroupItemComponent
  ],
  templateUrl: './groups-ui.component.html',
  styleUrl: './groups-ui.component.scss'
})
export class GroupsUiComponent extends ResponsiveBaseComponent implements OnInit {
  private customerService = inject(CustomerService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  groupsByCustomer = signal<CustomerGroupSection[]>([]);
  isLoading = signal(false);

  selectedRole: OperatorGroupRole | undefined = undefined;

  readonly roles = Object.values(OperatorGroupRole);

  ngOnInit(): void {
    this.loadGroups();
  }

  loadGroups(): void {
    this.isLoading.set(true);
    this.customerService.customerFilterPost(
      undefined, false, 0, 200, 'name', SortOrder.Ascending
    ).subscribe({
      next: result => {
        const sections: CustomerGroupSection[] = (result.items ?? [])
          .map((customer: CustomerDto) => ({
            customerId: customer.id ?? 'unknown',
            customerName: customer.name ?? 'Unknown Customer',
            groups: (customer.operatorGroups ?? []).filter(
              (g: OperatorGroupDto) => !this.selectedRole || g.role === this.selectedRole
            )
          }))
          .filter((s: CustomerGroupSection) => s.groups.length > 0);
        this.groupsByCustomer.set(sections);
        this.isLoading.set(false);
      },
      error: () => {
        this.snackBar.open('Failed to load customers', 'Close', { duration: 5000 });
        this.isLoading.set(false);
      }
    });
  }

  onFilterChange(): void {
    this.loadGroups();
  }

  get hasGroups(): boolean {
    return this.groupsByCustomer().some((s: CustomerGroupSection) => s.groups.length > 0);
  }

  onAssignParkingSpace(group: OperatorGroupDto): void {
    const dialogRef = this.dialog.open(AssignParkingspaceGroupComponent, {
      width: this.isHandset() ? '95vw' : '560px',
      maxWidth: '95vw',
      data: { group } as AssignParkingSpaceGroupDialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.loadGroups();
      }
    });
  }

  onUnassignParkingSpace(event: { group: OperatorGroupDto; parkingSpaceId: string }): void {
    const spaceName = event.group.parkingSpaceOperators
      ?.find(pso => pso.parkingSpaceId === event.parkingSpaceId)
      ?.parkingSpace?.name ?? event.parkingSpaceId;

    const dialogRef = this.dialog.open(UnassignParkingspaceGroupComponent, {
      width: this.isHandset() ? '95vw' : '480px',
      maxWidth: '95vw',
      data: {
        group: event.group,
        parkingSpaceId: event.parkingSpaceId,
        parkingSpaceName: spaceName
      } as UnassignParkingSpaceGroupDialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.loadGroups();
      }
    });
  }

  onAddOperator(group: OperatorGroupDto): void {
    const dialogRef = this.dialog.open(AddOperatorGroupComponent, {
      width: this.isHandset() ? '95vw' : '480px',
      maxWidth: '95vw',
      data: { group } as AddOperatorGroupDialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.loadGroups();
      }
    });
  }

  // get hasGroups(): boolean {
  //   return this.groupsByCustomer().some(s => s.groups.length > 0);
  // }
}
